import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Attachment, DownloadService, Endpoint, ProviderAccreditationPendenciesService, shouldRenderMobile, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { AttachmentsList, HpmsDynamicContentFieldApi } from '@philips-emr/hpms-provider-components';
import { AttachmentsListItem } from '@philips-emr/hpms-provider-components/lib/api/attachments-list-api';
import { CheckboxAPI, DateFieldAPI, FetchAPI, Field, FieldAPI, FileUploaderAPI, FormAPI, Link, ModalAPI, Panel, PanelAPI, Stamp, StampIcon, Text, ToastNotificationAPI, Token } from '@philips-emr/tws-components';
import { Color, LayoutAlign, Size, ToastLevel, Type } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { generateUUID, isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import './accreditation.css';

import { AccreditationDocumentsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './accreditation-update-controller';

interface Document {
  nrSeqTypeDoc: number,
  dsDocument: string,
  ieUndeterminedValidity: string,
  expirationDt: string,
  ieIssueDt: string,
  ieExpirationDt: string,
  nrSeqAttachment: number,
  issueDt: string,
  mandatory: string,
  editable: string,
  ieStatusCheck: boolean,
  filename: string,
  filepath: string,
  orientation: string,
}

enum IeUndeterminedValidity {
  MARKED = 'S',
  UNMARKED = 'N',
}

enum DocumentDateStatus {
  OPTIONAL = 'S',
  DO_NOT_REQUEST = 'N',
  MANDATORY = 'O',
  MANDATORIY_AND_UNDEFINED = 'I'
}

@Controller({ fragment: FragmentNames.ACCREDITATION_DOCUMENTS })
class AccreditationDocumentsController extends BaseController {
  private attachmentTemplate: Attachment | null = null;
  @Inject(Aliases.FETCH_DOCUMENTS)
  private fetchDocuments: FetchAPI;
  @Inject(Aliases.PANEL_FILE_TEMPLATES)
  private panelFileTemplates: PanelAPI;
  @Inject(Aliases.PANEL_DOCUMENT)
  private panelDocument: PanelAPI;
  @Inject(Aliases.FIELD_VALIDITY_DATE_DISABLED)
  private fieldValidityDateDisabled: FieldAPI;
  @Inject(Aliases.FIELD_VALIDITY_DATE)
  private fieldValidityDate: FieldAPI;
  @Inject(Aliases.DATE_FIELD_VALIDITY)
  private dateFieldValidity: DateFieldAPI;
  @Inject(Aliases.FIELD_EMISSION_DATE_DISABLED)
  private fieldEmissionDateDisabled: FieldAPI;
  @Inject(Aliases.FIELD_EMISSION_DATE)
  private fieldEmissionDate: FieldAPI;
  @Inject(Aliases.CHECKBOX_UNDETERMINED_VALIDITY)
  private checkboxUndeterminedValidity: CheckboxAPI;
  @Inject(Aliases.FILE_UPLOADER)
  private fileUploader: FileUploaderAPI;
  @Inject(Aliases.FORM_DOCUMENT)
  private formDocument: FormAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toastNotificationPendencies: ToastNotificationAPI;
  @Inject(Aliases.FIELD_FILE_UPLOADER)
  private fieldFileUploader: FieldAPI;
  @Inject(Aliases.FIELD_ORIENTATION)
  private fieldOrientation: FieldAPI;

  @OnMount(Aliases.PANEL_DOCUMENTS)
  onMountPanelDocuments(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.context.set({ nrSeqProvider, nrSeqAccreditation });
    this.fetchDocuments.forceFetch();
  }

  @OnMount(Aliases.LINK_FILE_TEMPLATES)
  onMountPanelFileTemplates(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.get<Attachment>(Endpoint.GET.FILE_TEMPLATES_DOCUMENTS, { nrSeqProvider, nrSeqAccreditation })
      .then(resp => {
        const { body = null } = resp;
        this.attachmentTemplate = body;
        this.panelFileTemplates.setVisible(Boolean(this.attachmentTemplate?.filename) && !shouldRenderMobile());
      })
      .catch(() => this.panelFileTemplates.setVisible(false));
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.DOCUMENTS);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toastNotificationPendencies, event);
    }
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_DOCUMENT)
  onRenderClauses(event: RenderEvent): void {
    const { data, render } = event;
    const { nrSeqTypeDoc, dsDocument, editable } = data as Document;
    if (!isEmptyArrayOrObject(data) && editable === 'S') {
      render((): ReactElement =>
        <Panel className="c-panel__documents_edit" uuid={generateUUID()} alignItems={LayoutAlign.END} >
          <Link value={MessageService.translate('provider.edit')} onClick={() => this.openModal(nrSeqTypeDoc, dsDocument)} size={Size.MEDIUM} />
        </Panel>);
    }
  }

  private openModal(nrSeqTypeDocument: number, dsDocument: string): void {
    this.panelDocument.setVisible(true);
    this.context.set({ nrSeqTypeDocument, dsDocument });
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_ATTACHMENT)
  onDynamicAttachment(event: RenderEvent): void {
    const api = event.target as HpmsDynamicContentFieldApi;
    const { data, render } = event;
    const { filename, nrSeqAttachment } = data as Attachment;
    api.setVisible(!!nrSeqAttachment);
    if (!isEmptyArrayOrObject(data)) {
      let attachmentDetail = {};
      attachmentDetail = [{
        label: filename,
        value: null,
      }];
      render((): ReactElement =>
        <Panel uuid={generateUUID()} className="">
          <Field width={12} uuid={generateUUID()} alwaysShowLabel label="provider.attachment" entry="filename" visible={!!nrSeqAttachment}>
            <AttachmentsList data={attachmentDetail as AttachmentsListItem[]} onSelect={() => this.downloadAttachment(filename, nrSeqAttachment)} />
          </Field>
        </Panel>
      );
    }
  }

  private downloadAttachment(filename: string, nrSeqAttachment: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    DownloadService.of({
      endpoint: Endpoint.GET.DOCUMENT_ATTACHMENT_DOWNLOAD,
      name: filename,
      parameters: { nrSeqProvider, nrSeqAccreditation, nrSeqAttachment },
    });
  }

  @OnClick(Aliases.LINK_FILE_TEMPLATES)
  onClick(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const name = this.attachmentTemplate?.filename;
    DownloadService.of({
      endpoint: Endpoint.GET.DOCUMENTS_FILE_TEMPLATE_DOWNLOAD,
      name,
      parameters: { nrSeqProvider, nrSeqAccreditation },
    });
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_EXPIRATION_DATE)
  onDynamicContentRenderExpirationDate(event: RenderEvent): void {
    const api = event.target as HpmsDynamicContentFieldApi;
    const { data, render } = event;
    const { ieUndeterminedValidity, expirationDt, nrSeqAttachment, mandatory } = data as Document;
    const fieldSize = nrSeqAttachment ? 3 : 5;
    api.setWidth(fieldSize);
    if (nrSeqAttachment) {
      api.setClassName('adjustments__content-field');
    }
    render((): ReactElement =>
      <Panel uuid={generateUUID()} className={this.getClassName()}>
        <Field width={12} uuid={generateUUID()} label="provider.validity-date" entry="" visible={!!nrSeqAttachment || !!expirationDt}>
          {ieUndeterminedValidity === IeUndeterminedValidity.MARKED ?
            <Text value={'provider.undetermined'} />
            :
            <Text value={expirationDt} formatters={{ value: { name: 'date' } }} />
          }
          {!expirationDt && <Text />}
        </Field>
        <Field width={8} uuid={generateUUID()} label="" entry="" visible={!nrSeqAttachment}>
          {mandatory === 'S' && <Token value="provider.mandatory" type={Type.NEUTRAL} />}
        </Field>
        <Field width={5} uuid={generateUUID()} label="" entry="" visible={!nrSeqAttachment}>
          <Stamp icon={StampIcon.WARNING} color={Color.SAFETY_ORANGE} label={MessageService.translate('provider.pending')} />
        </Field>
      </Panel>
    );
  }

  private getClassName(): string {
    return shouldRenderMobile() ? 'accreditation-documents-mobile__token' : 'accreditation-documents__custom-card';
  }

  @OnMount(Aliases.FIELD_ISSUE_DATE)
  onMountEmissionDate(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { nrSeqAttachment, issueDt } = api.getData() as unknown as Document;
    api.setVisible(!!nrSeqAttachment || !!issueDt);
  }

  @OnMount(Aliases.FETCH_DOCUMENT)
  onMountFetchDocument(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProvider, nrSeqAccreditation, nrSeqTypeDocument } = this.context.getBulkData();
    api.setParameters({ nrSeqProvider, nrSeqAccreditation, nrSeqTypeDocument });
    api.forceFetch();
  }

  @OnMount(Aliases.MODAL_DOCUMENT)
  onMountModalDocument(event: MountEvent): void {
    const api = event.target as ModalAPI;
    const { dsDocument } = this.context.getBulkData() as unknown as Document;
    api.setTitle(dsDocument);
  }

  @OnSubmit(Aliases.FORM_DOCUMENT)
  onSubmitFormDocument(event: SubmitEvent): void {
    const { record } = event;
    const data = { ...record };
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const newAttachment = data.attachments[0] as Attachment;
    data.ieUndeterminedValidity = IeUndeterminedValidity.UNMARKED;

    if (data.ieStatusCheck) {
      data.ieUndeterminedValidity = IeUndeterminedValidity.MARKED;
    }

    const body = { newAttachment, ...data };
    this.formDocument.setLocked(true);
    this.formDocument.setLoading(true);
    this.formDocument.setDisabled(true);
    HttpService.post(Endpoint.POST.ACCREDITATION_SEND_DOCUMENT, { nrSeqProvider, nrSeqAccreditation }, { body })
      .then(() => {
        this.panelDocument.setVisible(false);
        this.fetchDocuments.forceFetch();
      }).catch(() => {
        this.formDocument.setLoading(false);
        this.formDocument.setDisabled(false);
        this.formDocument.setLocked(false);
        ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR });
      });
  }

  @OnClose(Aliases.MODAL_DOCUMENT)
  onCloseModalDocument(): void {
    this.panelDocument.setVisible(false);
  }

  @OnCancel(Aliases.FORM_DOCUMENT)
  onCancelFormDocument(): void {
    this.panelDocument.setVisible(false);
  }

  @OnAfterFetch(Aliases.FETCH_DOCUMENT)
  onAfterFetchDocument(event: FetchEvent): void {
    if (!event.data) return;
    const { ieIssueDt, ieExpirationDt, nrSeqTypeDoc, dsDocument, ieStatusCheck, filename, filepath, orientation } = event.data as Document;
    switch (ieIssueDt) {
      case DocumentDateStatus.DO_NOT_REQUEST:
        this.fieldEmissionDate.setVisible(false);
        this.fieldEmissionDateDisabled.setVisible(true);
        break;
      case DocumentDateStatus.OPTIONAL:
        this.fieldEmissionDate.setVisible(true);
        this.fieldEmissionDate.setValidators([{ name: 'prevent-future-date' }] as Validator[]);
        this.fieldEmissionDateDisabled.setVisible(false);
        break;
      case DocumentDateStatus.MANDATORY:
        this.fieldEmissionDate.setVisible(true);
        this.fieldEmissionDateDisabled.setVisible(false);
        this.fieldEmissionDate.setValidators([{ name: 'required', options: {} }, { name: 'prevent-future-date' }] as Validator[]);
        break;
    }
    switch (ieExpirationDt) {
      case DocumentDateStatus.DO_NOT_REQUEST:
        this.fieldValidityDate.setVisible(false);
        this.fieldValidityDateDisabled.setVisible(true);
        this.checkboxUndeterminedValidity.setEnabled(false);
        break;
      case DocumentDateStatus.OPTIONAL:
        this.fieldValidityDate.setVisible(true);
        this.fieldValidityDateDisabled.setVisible(false);
        this.checkboxUndeterminedValidity.setEnabled(false);
        this.fieldValidityDate.setValidators([{ name: 'prevent-previous-date' }] as Validator[]);
        break;
      case DocumentDateStatus.MANDATORY:
        this.fieldValidityDate.setVisible(true);
        this.fieldValidityDateDisabled.setVisible(false);
        this.checkboxUndeterminedValidity.setEnabled(false);
        this.fieldValidityDate.setValidators([{ name: 'required', options: {} }, { name: 'prevent-previous-date' }] as Validator[]);
        break;
      case DocumentDateStatus.MANDATORIY_AND_UNDEFINED:
        this.fieldValidityDate.setVisible(true);
        this.fieldValidityDateDisabled.setVisible(false);
        this.checkboxUndeterminedValidity.setEnabled(ieExpirationDt === DocumentDateStatus.MANDATORIY_AND_UNDEFINED);
        this.fieldValidityDate.setValidators([{ name: 'required', options: {} }, { name: 'prevent-previous-date' }] as Validator[]);
        break;
    }
    if (ieStatusCheck) {
      this.fieldValidityDate.setVisible(false);
      this.fieldValidityDateDisabled.setVisible(true);
    }

    const predefinedAttachments = {
      key: String(nrSeqTypeDoc),
      value: dsDocument,
    };

    if (filename) {
      const attachment = {
        filename,
        filepath,
        size: Number(null),
        predefinedFileKey: String(nrSeqTypeDoc),
      };
      this.fileUploader.setValue([attachment]);
      this.fieldFileUploader.setValue([attachment]);
      this.fieldFileUploader.setValidators([]);
    }
    this.fieldOrientation.setVisible(!isEmptyArrayOrObject(orientation));
    this.fileUploader.setPredefinedFiles([predefinedAttachments]);
  }

  @OnChange(Aliases.CHECKBOX_UNDETERMINED_VALIDITY)
  onChangeCheckboxUndeterminedVaidity(event: ChangeEvent<{ checked: string }>): void {
    const checked = event.value;
    if (!checked) {
      this.fieldValidityDate.setValidators([{ name: 'required', options: {} }, { name: 'prevent-previous-date' }] as Validator[]);
      this.fieldValidityDate.setVisible(true);
      this.fieldValidityDateDisabled.setVisible(false);
      this.dateFieldValidity.setReadOnly(false);
    } else {
      this.dateFieldValidity.setState({ value: '' });
      this.fieldValidityDate.setData({});
      this.fieldValidityDate.setState({ showValidations: false });
      this.fieldValidityDate.removeValidator('required');
      this.fieldValidityDate.setVisible(false);
      this.fieldValidityDateDisabled.setVisible(true);
    }
  }
}

export {
  AccreditationDocumentsController,
  DocumentDateStatus,
};
